import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { bookingResponseStatus, DEFAULT_DEPOSIT_PERCENT } from "./booking";

const createSchema = z.object({
  name: z.string().trim().min(2).max(80),
  email: z.string().trim().toLowerCase().email().max(120),
  phone: z.string().trim().max(20),
  postcode: z.string().trim().max(12),
  address: z.string().trim().max(200),
  service: z.string().trim().max(80),
  details: z.string().trim().max(2000),
  preferredDate: z.string().trim().max(40),
  estimate: z.string().trim().max(200),
  suggestedPrice: z.number().min(0).max(100000).nullable(),
});

/** Public: called by the quote form to create a draft booking for the admin to review. */
export const createBookingFromQuote = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => createSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("bookings").insert({
      client_name: data.name,
      client_email: data.email,
      client_phone: data.phone,
      postcode: data.postcode,
      address: data.address,
      service: data.service,
      job_details: data.details,
      preferred_date: data.preferredDate,
      job_date: data.preferredDate,
      estimate_text: data.estimate,
      final_price: data.suggestedPrice,
      status: "new",
      deposit_percent: DEFAULT_DEPOSIT_PERCENT,
    });
    if (error) console.error("[bookings] create failed", error.message);
    return { ok: !error };
  });

const tokenSchema = z.object({ token: z.string().regex(/^[a-f0-9]{36}$/) });

const PUBLIC_COLS =
  "id, created_at, sent_at, client_name, service, address, postcode, job_details, job_date, final_price, deposit_percent, status, accepted_at, confirmed_at";

/** Public: the client's booking document, only once the admin has sent it. */
export const getBookingByToken = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("bookings")
      .select(PUBLIC_COLS)
      .eq("token", data.token)
      .in("status", ["sent", "accepted", "confirmed", "rejected"])
      .maybeSingle();
    return row ? { ...row, final_price: row.final_price === null ? null : Number(row.final_price) } : null;
  });

/** Public: client accepts the booking and says the deposit has been paid. */
export const acceptBooking = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("bookings")
      .update({ status: bookingResponseStatus("paid"), accepted_at: new Date().toISOString() })
      .eq("token", data.token)
      .eq("status", "sent")
      .select("id");
    if (error) throw new Error("Could not save your acceptance. Please try again.");
    return { ok: (rows ?? []).length > 0 };
  });

/** Public: a token holder can reject only a still-pending quotation. */
export const rejectBooking = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.from("bookings")
      .update({ status: bookingResponseStatus("rejected") })
      .eq("token", data.token).eq("status", "sent").select("id");
    if (error) throw new Error("Could not save your response. Please try again.");
    return { ok: (rows ?? []).length > 0 };
  });

/** Team only: email the client their booking link or the "you're booked" notice. */
export const emailBookingToClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid(), kind: z.enum(["confirmation", "booked"]), origin: z.string().url() }).parse(d))
  .handler(async ({ data, context }) => {
    // RLS: only team members can read bookings.
    const { data: b } = await context.supabase.from("bookings").select("*").eq("id", data.id).maybeSingle();
    if (!b) throw new Error("Booking not found.");
    const { BANK_DETAILS, depositAmount, formatGBP, paymentReference } = await import("./booking");
    const { sendTemplateEmail } = await import("./email-templates/send-email");
    const price = Number(b.final_price ?? 0);
    const origin = /^https:\/\/(www\.)?izhubcleaning\.uk$|lovable\.app$|^http:\/\/localhost/.test(data.origin) ? data.origin : "https://izhubcleaning.uk";
    const res = await sendTemplateEmail("booking-email", b.client_email, {
      idempotencyKey: `booking-${data.kind}-${b.id}-${b.final_price}-${b.job_date}`,
      templateData: {
        kind: data.kind,
        firstName: b.client_name.split(" ")[0],
        service: b.service,
        jobDate: b.job_date,
        price: formatGBP(price),
        deposit: formatGBP(depositAmount(price, b.deposit_percent)),
        depositPercent: b.deposit_percent,
        accountName: BANK_DETAILS.accountName,
        sortCode: BANK_DETAILS.sortCode,
        accountNumber: BANK_DETAILS.accountNumber,
        reference: paymentReference(b.client_name, b.job_date),
        link: `${origin}/booking/${b.token}`,
      },
    });
    return { sent: res.sent };
  });
